import { useEffect, useRef } from "react";
import { HandWavingIcon } from "@phosphor-icons/react";
import { motion, useAnimationControls, useReducedMotion } from "framer-motion";
import { useI18N } from "common/hooks";

import { useOnboardingFlow } from "../state/useOnboardingFlow";
import { useDockedFootprint } from "../utils/useDockedFootprint";

/**
 * The docked sheet collapsed (`16089:97779`): "Personalise Sociability (2/3)"
 * over a progress bar, bottom-right on every page until the flow is done.
 *
 * One button — the whole card opens the sheet again. The frame carries a
 * hidden ✕; it stays hidden, because the flow has no way to be put away for
 * good from here, only finished.
 *
 * **The count is steps finished, not the step on screen.** The frame reads
 * "(2/3)" with the bar about two thirds full while the sheet behind it is on
 * "Step 3 of 3" — so the pill says how much is done. On step 1 that is 0, and
 * the bar is empty.
 *
 * Takes focus when it replaces the sheet's "–", so a keyboard user who
 * collapses the sheet is not dropped back at the top of the page. Not when it
 * is what the page loads with: nobody asked for it then.
 *
 * **It nudges.** Every `NUDGE_INTERVAL_MS` the card hops and the hand waves,
 * to draw the eye back to an unfinished flow - @see `NUDGE_LIMIT` for why it
 * stops.
 */
const OnboardingDockedPill = () => {
  const i18n = useI18N();
  const { finishedSteps, totalSteps, expand, isEngaged } = useOnboardingFlow();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const cardControls = useAnimationControls();
  const handControls = useAnimationControls();
  // Hovered: the user is already on it, so it holds still. Not focus - the
  // pill takes focus itself when the sheet collapses into it (@see the note
  // above), so holding on focus stopped it nudging for exactly the people who
  // had just minimised it.
  const heldRef = useRef(false);
  const hold = () => {
    heldRef.current = true;
  };
  const release = () => {
    heldRef.current = false;
  };
  useDockedFootprint("pill");
  useNudge(cardControls, handControls, heldRef);

  const finished = finishedSteps;
  const progress = totalSteps > 0 ? (finished / totalSteps) * 100 : 0;

  useEffect(() => {
    if (isEngaged) buttonRef.current?.focus();
    // Mount only - @see the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.button
      ref={buttonRef}
      type="button"
      onClick={expand}
      animate={cardControls}
      onPointerEnter={hold}
      onPointerLeave={release}
      aria-expanded="false"
      className="focus-blue flex w-[336px] max-w-[calc(100vw-32px)] items-center gap-2 rounded-lg border border-border-token-brand-minimal bg-container-brand-minimal px-4 py-3 text-left shadow-[0px_1px_2px_0px_rgba(7,7,8,0.08),0px_2px_6px_0px_rgba(7,7,8,0.12)] transition-colors hover:bg-bg-brand-ghost-hover"
    >
      {/* Pivots near the wrist, so the wave reads as a wave, not a spin. */}
      <motion.span
        aria-hidden="true"
        className="inline-flex shrink-0"
        animate={handControls}
        style={{ transformOrigin: "70% 85%" }}
      >
        <HandWavingIcon size={24} className="text-fg-neutral-strong" />
      </motion.span>

      <span className="flex min-w-0 flex-1 flex-col gap-1 px-1">
        <span className="body-m-medium text-fg-neutral-strong">
          {i18n
            .get("onboardingDockedTitle")
            .replace("{current}", String(finished))
            .replace("{total}", String(totalSteps))}
        </span>

        {/* Decorative: the title already says how far along this is. */}
        <span
          aria-hidden="true"
          className="relative block h-2 w-full overflow-hidden rounded-full bg-fg-neutral-minimal"
        >
          {/* Fills from empty each time the pill appears, once it has
              landed - a small "this is how far you got". */}
          <motion.span
            className="absolute inset-y-0 left-0 rounded-full bg-fg-brand-subtle"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{
              duration: 0.6,
              delay: 0.25,
              ease: [0.05, 0.7, 0.1, 1],
            }}
          />
        </span>

        <span className="body-s-regular truncate text-fg-neutral-subtle">
          {i18n.get("onboardingDockedSubtitle")}
        </span>
      </span>
    </motion.button>
  );
};

/** How often the pill nudges. */
const NUDGE_INTERVAL_MS = 5000;

/**
 * How many nudges each appearance of the pill gets before it holds still for
 * good.
 *
 * WCAG 2.2.2 (Pause, Stop, Hide - level A): motion that starts on its own and
 * goes on past five seconds needs a way for the user to stop it, and the pill
 * has none. Nudging forever would fail it; a short run that ends by itself is
 * the defensible version. Raise with care.
 */
const NUDGE_LIMIT = 3;

type AnimationControls = ReturnType<typeof useAnimationControls>;

/**
 * Runs the nudge on a timer. Skips a beat - without counting it - while the
 * pill is hovered, or the tab is in the background, and never runs
 * at all when the OS asks for reduced motion.
 */
function useNudge(
  cardControls: AnimationControls,
  handControls: AnimationControls,
  heldRef: React.MutableRefObject<boolean>,
) {
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (reduceMotion) return;
    let count = 0;
    const id = window.setInterval(() => {
      if (heldRef.current || document.hidden) return;
      count += 1;
      void cardControls.start({
        y: [0, -6, 0, -3, 0],
        transition: { duration: 0.7, ease: "easeOut" },
      });
      void handControls.start({
        rotate: [0, 18, -10, 18, -6, 12, 0],
        transition: { duration: 1, ease: "easeInOut" },
      });
      if (count >= NUDGE_LIMIT) window.clearInterval(id);
    }, NUDGE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [reduceMotion, cardControls, handControls, heldRef]);
}

export default OnboardingDockedPill;
